﻿import { NavDataType } from '@fe/core';
import { SVGIcon } from '@progress/kendo-svg-icons';
import dayjs from 'dayjs';
import { EventBus } from '../../../app/contexts/event-bus';
import useMap, { MapExtended } from '../../../app/hooks/use-map';
import useIntegration, { IntegrationInfo } from '../../../hooks/use-integration';
import useMapInteraction from '../../../hooks/use-map-interaction';
import { DrawingPolygonOutline, DrawingPolylineOutline } from '../../../icons/icons';
import { NavDataContextMenuContent, NavDataContextMenuData } from '../../../services/map-interaction-service';
import { Menu, MenuItem } from '../../common/menu/DropdownMenu';
import { FPMFillColor, FPMLineColor } from '../../drawing-object/DrawingObjectEditWindow';
import { DrawingObjectCategory, DrawingObjectFillStyle, DrawingObjectType } from '../../drawing-object/types';

type OnClickEvent = {
    option: ContextMenuOption;
    item: NavDataContextMenuContent;
    polygon?: GeoJSON.Position[];
};

export function NavDataContextMenu(props: { info: NavDataContextMenuData }) {
    const context = props.info;
    const interactionService = useMapInteraction();
    const integrationInfo = useIntegration();
    const map = useMap();

    const handleOnClick = async (e: OnClickEvent) => {
        interactionService.dismissContextMenu();
        EventBus.fire('hide-all-previews', null);

        if (e.option === ContextMenuOption.createDrawingObject) {
            if (!e.polygon || e.polygon.length < 3) {
                return;
            }
            EventBus.fire('draw.create', {
                drawObject: {
                    type: DrawingObjectType.polygon,
                    points: e.polygon
                }
            });
        } else if (e.option === ContextMenuOption.createFPMAvoidanceArea) {
            if (!e.polygon || e.polygon.length < 3) {
                return;
            }
            const now = dayjs();
            EventBus.fire('draw.create', {
                drawObject: {
                    name: `Avoidance_${now.utc().format('YYYYMMDD_HHmmss')}Z`,
                    label: {
                        text: `Avoidance_${now.utc().format('YYYYMMDD_HHmmss')}Z`
                    },
                    type: DrawingObjectType.polygon,
                    points: e.polygon,
                    category: DrawingObjectCategory.avoidance,
                    fpmProperties: {
                        sync: true
                    },
                    line: {
                        color: FPMLineColor
                    },
                    fill: {
                        color: FPMFillColor,
                        style: DrawingObjectFillStyle.diagHatchLeft
                    }
                }
            });
        }
    };

    return (
        <>
            {context.items.map((x, i) => (
                <div key={i} style={{ fontSize: 12 }} className="d-flex flex-column bg-color">
                    <div className="px-2 pt-1 d-flex gap-1 align-items-center fw-bold">{getTitle(x)}</div>
                    <NavDataContextMenuOptions item={x} onClick={handleOnClick} integrationInfo={integrationInfo} map={map} />
                </div>
            ))}
        </>
    );
}

enum ContextMenuOption {
    createDrawingObject,
    createFPMAvoidanceArea
}

const menuOptions: Record<ContextMenuOption, { name: string; icon?: SVGIcon }> = {
    [ContextMenuOption.createDrawingObject]: { name: 'Create drawing object', icon: DrawingPolygonOutline },
    [ContextMenuOption.createFPMAvoidanceArea]: { name: 'Create FPM Avoidance area ', icon: DrawingPolylineOutline }
};

const contextMenuOptionsList: Partial<Record<NavDataType, ContextMenuOption[]>> = {
    [NavDataType.airspace]: [ContextMenuOption.createDrawingObject, ContextMenuOption.createFPMAvoidanceArea],
    [NavDataType.sua]: [ContextMenuOption.createDrawingObject, ContextMenuOption.createFPMAvoidanceArea]
};

const getNavDataType = (layerName: string): NavDataType | null => {
    const layer = layerName.toLowerCase();
    if (layer.includes('airport')) {
        return NavDataType.airport;
    }
    if (layer.includes('airspace')) {
        return NavDataType.airspace;
    }
    if (layer.includes('airway')) {
        return NavDataType.airway;
    }
    if (layer.includes('fix')) {
        return NavDataType.fixes;
    }
    if (layer.includes('flightServiceStation')) {
        return NavDataType.flightServiceStation;
    }
    if (layer.includes('holding')) {
        return NavDataType.holding;
    }
    if (layer.includes('gridMora')) {
        return NavDataType.gridMora;
    }
    if (layer.includes('navaid')) {
        return NavDataType.navaids;
    }
    if (layer.includes('sid')) {
        return NavDataType.sids;
    }
    if (layer.includes('star')) {
        return NavDataType.stars;
    }
    if (layer.includes('sua')) {
        return NavDataType.sua;
    }
    if (layer.includes('timeZone')) {
        return NavDataType.timeZone;
    }
    if (layer.includes('weatherStation')) {
        return NavDataType.weatherStation;
    }
    return null;
};

const getTitle = (item: NavDataContextMenuContent) => {
    const typeNameMap: Record<NavDataType, string> = {
        [NavDataType.airport]: 'Airport',
        [NavDataType.airspace]: 'Airspace',
        [NavDataType.airway]: 'Airway',
        [NavDataType.fixes]: 'Fix',
        [NavDataType.flightServiceStation]: 'Flight Service Station',
        [NavDataType.holding]: 'Holding',
        [NavDataType.gridMora]: 'Grid MORA',
        [NavDataType.navaids]: 'Navaid',
        [NavDataType.sids]: 'SID',
        [NavDataType.stars]: 'STAR',
        [NavDataType.sua]: 'SUA',
        [NavDataType.timeZone]: 'Time Zone',
        [NavDataType.weatherStation]: 'Weather Station'
    };
    const id = item.properties?.name || item.properties?.id || 'Unknown';
    const navDataType = getNavDataType(item.layerName);
    const typeName = navDataType !== null ? typeNameMap[navDataType] : 'Unknown';
    return `${typeName}: ${id}`;
};

function NavDataContextMenuOptions(props: {
    item: NavDataContextMenuContent;
    onClick: (e: OnClickEvent) => void;
    integrationInfo: IntegrationInfo;
    map: MapExtended | null;
}) {
    const navDataType = getNavDataType(props.item.layerName);
    if (!navDataType) {
        return <></>;
    }

    const options = contextMenuOptionsList[navDataType];
    if (!options || options.length === 0) {
        return <></>;
    }

    const handleMouseLeave = () => {
        if (!props.map) {
            return;
        }

        EventBus.fire('hide-preview', {
            id: `${props.item.layerName}`
        });
    };

    const hoverPolygon = (coords: GeoJSON.Position[][]) => {
        if (!props.map) {
            return;
        }
        EventBus.fire('show-preview', {
            id: `${props.item.layerName}`,
            coordinates: coords[0] // TODO: is [0] ok?
        });
    };

    return options.map((option, i) =>
        props.item.geometry.type === 'MultiPolygon' ? (
            <Menu
                key={i}
                label={menuOptions[option].name}
                icon={menuOptions[option].icon}
                disabled={
                    option === ContextMenuOption.createFPMAvoidanceArea && !props.integrationInfo.isActionEnabled('canSendDrawObjToFPM')
                }>
                {props.item.geometry.coordinates.map((x, i) => (
                    <MenuItem
                        key={i}
                        label={`Polygon ${i + 1}`}
                        onClick={() => props.onClick({ option, item: props.item, polygon: x[0] })}
                        onMouseEnter={() => hoverPolygon(x)}
                        onMouseLeave={handleMouseLeave}></MenuItem>
                ))}
            </Menu>
        ) : (
            <MenuItem
                key={i}
                onClick={() =>
                    props.onClick({
                        option,
                        item: props.item,
                        polygon: props.item.geometry.type === 'Polygon' ? props.item.geometry.coordinates[0] : undefined
                    })
                }
                label={menuOptions[option].name}
                icon={menuOptions[option].icon}
                onMouseEnter={() => props.item.geometry.type === 'Polygon' && hoverPolygon(props.item.geometry.coordinates)}
                onMouseLeave={handleMouseLeave}
                disabled={
                    option === ContextMenuOption.createFPMAvoidanceArea && !props.integrationInfo.isActionEnabled('canSendDrawObjToFPM')
                }
                hint={
                    option === ContextMenuOption.createFPMAvoidanceArea
                        ? props.integrationInfo.getDisabledReason('canSendDrawObjToFPM')
                        : null
                }
            />
        )
    );
}
